import { Body, VStack } from '@hover/blueprint';
import { StaticImage } from 'gatsby-plugin-image';
import React from 'react';
import { ICareersOurTool } from '../interfaces/ICareersOurTool';
import { IFaqQuestion } from '../interfaces/IFaqQuestion';

export const OUR_TOOLS: ICareersOurTool[] = [
  {
    id: 0,
    image: (
      <StaticImage
        loading="lazy"
        placeholder="blurred"
        src="../assets/images/careers_our_tools_1.png"
        alt="Growth, Learning, and Development"
      />
    ),
    title: 'Growth, learning, and development',
    body: 'HOVER’s robust L&D program supports the greater culture of learning that exists at HOVER. Employees are given the tools and time to learn new skills, hear from industry experts, and advance in their careers. Employees personalize their growth with regular in-house offerings, dedicated time for self-directed learning, leadership coaching, generous per-employee funding for outside opportunities, and more.',
  },
  {
    id: 1,
    image: (
      <StaticImage
        loading="lazy"
        placeholder="blurred"
        src="../assets/images/careers_our_tools_2.png"
        alt="Diversity and Inclusion"
      />
    ),
    title: 'Diversity and inclusion',
    body: 'A diverse HOVER where employees experience true inclusion is good for employees and critical to our success. From recruitment practices that reduce the impact of systemic inequities to supporting the exemplary educational, policy, and community work of our employee-led D&I Committee, we are committed to addressing bias and fostering belonging at HOVER.',
  },
  {
    id: 2,
    image: (
      <StaticImage
        loading="lazy"
        placeholder="blurred"
        src="../assets/images/careers_our_tools_3.png"
        alt="Wellness"
      />
    ),
    title: 'Wellness',
    body: 'We believe in work/life balance. We offer flexible work policies and seek to build warm, collegial relationships with one another regardless of role or location. We have monthly “Self-Care Days,” a paid day off, and a competitive suite of Health & Wellness benefits to support employees and their families at all stages of life.',
  },
];

export const OUR_VALUES = [
  {
    id: 0,
    title: 'THINK.',
    description:
      'To evolve how home improvement happens for homeowners and professionals, we share our diverse perspectives, question assumptions, and push each other to innovate.',
  },
  {
    id: 1,
    title: 'DO.',
    description:
      'We have a strong bias towards action. We work to transform great thinking into products that wow our customers, and we learn and adapt along the way.',
  },
  {
    id: 2,
    title: 'SERVE.',
    description:
      'We are guided by the principles of servant leadership and transparency. Whether working with colleagues, serving our customers, or engaging in community work, empowering others through service is part of our core.',
  },
];

export const CAREERS_FAQ_ITEMS: Array<IFaqQuestion> = [
  {
    id: 0,
    title: 'Q: What does your interview process consist of?',
    content: (
      <VStack spacing={300} mb={500}>
        <Body alignSelf="flex-start" size={500}>
          A: Our process generally goes like this:
        </Body>
        <VStack flexDir="column" spacing={400}>
          <Body size={500}>
            We review your application, looking for applicable experience and
            attributes we believe would make you successful in the role.
          </Body>
          <Body size={500}>
            You’ll have an introductory call where we’ll provide an overview of
            HOVER and the role. We’ll also begin to assess your qualifications
            and answer any questions you may have.
          </Body>
          <Body size={500}>
            You&apos;ll meet with your new manager or another team member. Here
            we’ll do a deeper dive into your background, which could include a
            live technical assessment or take-home challenge to be completed
            when convenient for you.
          </Body>
          <Body size={500}>
            Our virtual onsite interview is usually the final step in the
            interview process. You’ll meet with multiple members of the
            immediate team you’re interviewing with as well as a
            cross-functional teammate or two. We’ll assess technical and
            non-technical competencies as well as values alignment.
          </Body>
        </VStack>
        <Body size={500}>
          Please note that this is a generalization, and for a more specific
          understanding of the interview process for the role you’ve applied to
          please ask your Recruiter. Our goal is to provide ample opportunity
          for you to demonstrate your ability to succeed in the role. We believe
          the best interview processes are objective, transparent and
          consistent, and that is what we aim to provide you with.{' '}
        </Body>
      </VStack>
    ),
  },
  {
    id: 1,
    title: 'Q: What is your current work from home policy?',
    content: (
      <VStack spacing={400} mb={500}>
        <Body size={500}>
          A: We see value in having offices where employees gather and
          collaborate. We also know that employees want more flexibility in
          their work schedules so we practice a hybrid model.
        </Body>
        <Body size={500}>
          To us, hybrid means that all employees will either be ‘in office’ or
          ‘fully remote.’ Employees who are in office will be expected to go
          into the office at least 2-3 days / week. Those who are fully remote
          will have opportunities to travel to our offices at certain times.
          Some employees will need to be on-site more based on the nature of
          their roles. We will not define success by total time spent in the
          office.
        </Body>
      </VStack>
    ),
  },
  {
    id: 2,
    title: 'Q: Will my interview be in-person or remote?',
    content: (
      <Body size={500} mb={500}>
        A: All of our interviews are still being conducted remotely. We are
        prioritizing the wellbeing of our employees and candidates, as well as
        trying to provide a consistent interview process for all candidates. We
        primarily use Zoom video conferencing for our interview format.
      </Body>
    ),
  },
  {
    id: 3,
    title: 'Q: What is HOVER’s compensation philosophy?',
    content: (
      <Body size={500} mb={500}>
        A: Our goal is to have transparent, equitable, rigorous, and responsible
        compensation practices. We take a market based approach and use
        best-in-class compensation data sources to help define compensation
        bands for each role, level and location. While being paid fairly and
        competitively are important, we believe compensation is only one part of
        a much larger picture of why you join and stay at HOVER. The most
        meaningful thing we can provide is an exceptional work experience.
      </Body>
    ),
  },
  {
    id: 4,
    title: 'Q: How should I prepare for my HOVER interview?',
    content: (
      <Body size={500} mb={500}>
        A: Ahead of panel interviews with our team, your recruiter will schedule
        a call to go over what to expect and any role specific information.
        We’ll also share some additional resources along the way that include
        information about our company’s history, values, and general
        interviewing best practices. One of the best ways you can prepare for
        your interview and conversations with our team is to download the HOVER
        App and experience our product for yourself.
      </Body>
    ),
  },
  {
    id: 5,
    title: 'Q: Does HOVER have an intern program?',
    content: (
      <Body size={500} mb={500}>
        A: Yes, we absolutely have an intern program. Our summer 2021 class of
        interns were hired across six departments and enriched HOVER with
        diversity, intelligence and drive. We’ve converted several interns to
        full-time employees over the years, which is something we’re really
        proud of and expect to do more of in the future.
      </Body>
    ),
  },
  {
    id: 6,
    title: 'Q: What benefits does HOVER offer employees?',
    content: (
      <Body size={500} mb={500}>
        A: We offer employees competitive salaries, meaningful equity and
        comprehensive medical, dental and vision benefits. We have a flexible
        vacation policy and employees have access to wellbeing and learning
        resources like management training, conferences, and online learning
        platforms. We support families and have generous parental leave benefits
        and help employees navigate how to use them. We equip our teams with the
        best tools, give a stipend to purchase the right gear for working at
        home, and much more.
      </Body>
    ),
  },
];
